/**
 * Pure nested-space slot geometry.
 *
 * A container widget composes a `[part="subspace"]` slot; the flex solver
 * assigns it an exact rect. This is the SAME geometry the live board used to
 * re-measure with a per-nested-space ResizeObserver and the SVG export already
 * extracts — so it is computed once, from the scene, with NO DOM. Both backends
 * (live board `NestedSpaceFit`, headless `render-svg`) consume this one helper,
 * upholding the project law that no diagram geometry is DOM-measured.
 */
import { layout, type LayoutBox, type Rect } from "../layout.js";
import { metricsMeasure } from "../metrics.js";
import type { Measure } from "../text.js";
import { composeBoardNode } from "./compose-node.js";
import type { BoardItem } from "./types.js";

/** The `[part="subspace"]` slot's laid-out rect in a composed node (item-local
 *  coords) — where the host projects the nested space. Depth-first; first match. */
export function findSubspaceSlot(lb: LayoutBox): Rect | undefined {
  if (lb.node.type === "slot" && lb.node.part === "subspace") return lb.rect;
  for (const c of lb.children) {
    const r = findSubspaceSlot(c);
    if (r) return r;
  }
  return undefined;
}

/**
 * Solve a container item's nested-space slot rect PURELY from scene geometry —
 * item-local px, the rect the host projects its nested preview into. Compose the
 * widget (the slot appears when the item carries a placed subspace), lay it out
 * at the item's box, and find the subspace slot. Falls back to the body region
 * (~lower 70%) for a widget that exposes no slot — the same widget-agnostic
 * fallback the SVG export uses.
 *
 * Memoised per item: a full compose + flex + text-measure is far too heavy to
 * re-run on every live-board render (hover/selection re-renders every visible
 * container). Sound because `BoardItem` is immutable — every solve mints new
 * objects — so identity fixes the geometry inputs. The cache applies only to
 * the default measure; an injected measure bypasses it.
 */
const slotRectCache = new WeakMap<BoardItem, Rect>();

export function subspaceSlotRect(item: BoardItem, measure: Measure = metricsMeasure): Rect {
  if (measure !== metricsMeasure) return solveSubspaceSlotRect(item, measure);
  let rect = slotRectCache.get(item);
  if (!rect) {
    rect = solveSubspaceSlotRect(item, measure);
    slotRectCache.set(item, rect);
  }
  return rect;
}

function solveSubspaceSlotRect(item: BoardItem, measure: Measure): Rect {
  const node = composeBoardNode(item);
  if (node) {
    const lb = layout(node, { x: 0, y: 0, w: item.w, h: item.h }, measure);
    const slot = findSubspaceSlot(lb);
    if (slot) return slot;
  }
  const pad = item.w * 0.06;
  return { x: pad, y: item.h * 0.28, w: item.w - 2 * pad, h: item.h * 0.72 - pad };
}
